/**
 * Google's tags in "Advanced" consent mode still send requests when the visitor hasn't agreed:
 * cookieless, marked with the consent state (gcs=G1xy: x ads, y analytics; 0 denied), but carrying
 * the IP address, device and page. Where the law says ask first that is contested, so by default
 * those requests don't leave the page: Google gets nothing until the visitor says yes ("Basic").
 * Requests Google marks as consented pass untouched, and the site owner can allow the pings.
 *
 * Installed synchronously at script start, before Google's tags load, since they keep references to
 * fetch and sendBeacon.
 */

const ADS = /(^|\.)(doubleclick\.net|googleadservices\.com|googlesyndication\.com|google\.com|google\.[a-z.]+)$/;
const ANALYTICS = /(^|\.)(google-analytics\.com|analytics\.google\.com)$/;
/**
 * Sites can send Google's requests through their own address (Squarespace's /metrics/gs/…, Google's
 * tag gateway, server-side tagging), so on any other host, recognise them by Google's own paths.
 */
const ADS_PATH = /\/(pagead\/(conversion|viewthroughconversion|1p-conversion|1p-user-list|landing)|measurement\/conversion|ccm\/collect|activity;)/;
const ANALYTICS_PATH = /\/g\/collect$/;

/** A request Google's tags made without the consent it needs. */
export function isDeniedPing(raw: string, base = location.href): boolean {
  let u: URL;
  try {
    u = new URL(raw, base);
  } catch {
    return false;
  }
  const gcs = /(?:^|[?&;])gcs=G1([01-])([01-])/.exec(u.search + u.pathname)?.slice(1);
  if (!gcs) return false;
  const [ads, analytics] = gcs;
  if (ANALYTICS.test(u.hostname)) return analytics === '0';
  if (ADS.test(u.hostname)) return ads === '0';
  if (ANALYTICS_PATH.test(u.pathname)) return analytics === '0';
  if (ADS_PATH.test(u.pathname)) return ads === '0';
  return false;
}

let allowed = false;
let installed = false;

/** The site owner chose Advanced: let the pings go. */
export function allowPings(): void {
  allowed = true;
}

const drop = (url: unknown) => !allowed && typeof url === 'string' && isDeniedPing(url);
const urlOf = (input: unknown): string | undefined =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : typeof Request !== 'undefined' && input instanceof Request ? input.url : undefined;

export function holdPings(): void {
  if (installed) return;
  installed = true;
  try {
    const beacon = navigator.sendBeacon?.bind(navigator);
    // Report success: the tag has nothing to retry.
    if (beacon) navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => (drop(urlOf(url)) ? true : beacon(url, data));
    const f = window.fetch?.bind(window);
    if (f) window.fetch = (input: RequestInfo | URL, init?: RequestInit) => (drop(urlOf(input)) ? Promise.resolve(new Response(null, { status: 204 })) : f(input, init));
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
      // An aborted-before-send request fails quietly; point it nowhere instead.
      const target = drop(urlOf(url)) ? 'data:,' : url;
      return (open as (...a: unknown[]) => void).call(this, method, target, ...rest);
    } as typeof open;
    const src = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
    if (src?.set && src.get) {
      Object.defineProperty(HTMLImageElement.prototype, 'src', {
        ...src,
        set(this: HTMLImageElement, v: string) {
          if (!drop(String(v))) src.set!.call(this, v);
        },
      });
    }
  } catch {
    /* a locked-down page: Google's pings go as they would without us */
  }
}
